import { firebaseFunctions } from '@rabbit/firebase/adapter-react';
import { globalApiPrefix } from '@rabbit/firebase/doctype';

function getFunctionBaseUrl(): string {
  const FIREBASE_MODE = import.meta.env['VITE_FIREBASE_MODE'];
  const overriddenBase = import.meta.env['VITE_API_BASE_URL'] as
    | string
    | undefined;
  const isEmulatorMode = String(FIREBASE_MODE || '')
    .toUpperCase()
    .includes('EMULATOR');

  const projectId =
    firebaseFunctions?.app?.options?.projectId ||
    import.meta.env['VITE_FIREBASE_PROJECT_ID'] ||
    'firerabbit-8e682';
  const region =
    (firebaseFunctions as any)?.region ||
    import.meta.env['VITE_CLOUD_REGION'] ||
    'europe-west1';

  return (
    overriddenBase ||
    (isEmulatorMode
      ? `http://localhost:5001/${projectId}/${region}/api`
      : window.location.hostname === 'localhost'
        ? `https://${region}-${projectId}.cloudfunctions.net/api`
        : '/api')
  );
}

function buildFilesProxyUrl(params: {
  url: string;
  filename?: string;
  disposition?: 'inline' | 'attachment';
  redirect?: boolean;
}): string {
  const functionBaseUrl = getFunctionBaseUrl();
  const base = new URL(functionBaseUrl, window.location.href);
  const trimmedPathname = base.pathname.replace(/\/+$/, '');

  /**
   * Important:
   * - On Firebase Hosting, we typically call same-origin `/api/...` and Hosting rewrites
   *   that to the `api` function. In that case the request path already starts with `/api`
   *   (Nest global prefix), so we must NOT append `/${globalApiPrefix}` again.
   * - When calling the function directly (emulator or `cloudfunctions.net`), the base URL
   *   points at the function name (`.../api`) and we DO need to append `/${globalApiPrefix}`.
   */
  const isSameOriginHostingRewrite =
    base.origin === window.location.origin &&
    trimmedPathname === `/${globalApiPrefix}`;

  const apiBaseUrl = isSameOriginHostingRewrite
    ? base.toString().replace(/\/+$/, '')
    : `${base.toString().replace(/\/+$/, '')}/${globalApiPrefix}`;
  const disposition = params.disposition || 'attachment';
  const redirect = params.redirect === true ? '1' : '0';
  return `${apiBaseUrl}/files/download?url=${encodeURIComponent(
    params.url
  )}&filename=${encodeURIComponent(params.filename || 'download')}&disposition=${encodeURIComponent(disposition)}&redirect=${encodeURIComponent(redirect)}`;
}

/**
 * Opens a file in a new tab via the backend proxy. Uses `Content-Disposition: inline`
 * so images/videos render in the tab instead of downloading.
 */
export function openFileInNewTab(url: string, filename?: string): void {
  // Prefer redirect so large media (e.g. mp4) loads directly from Storage.
  const proxyUrl = buildFilesProxyUrl({
    url,
    filename,
    disposition: 'inline',
    redirect: true,
  });
  const a = document.createElement('a');
  a.href = new URL(proxyUrl, window.location.href).toString();
  a.target = '_blank';
  a.rel = 'noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function deriveFilenameFromUrl(inputUrl: string): string | undefined {
  try {
    const u = new URL(inputUrl);
    // Prefer last pathname segment if present.
    const last = u.pathname.split('/').filter(Boolean).pop();
    if (last) return decodeURIComponent(last);
  } catch {
    // ignore
  }
  return undefined;
}

/**
 * Downloads a file from a URL using the backend API endpoint to avoid CORS issues.
 * The backend API proxies the Firebase Storage download and handles authentication.
 *
 * @param url - The URL of the file to download (typically a Firebase Storage URL)
 * @param filename - The desired filename for the downloaded file
 * @returns Promise that resolves when the download is triggered
 */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  try {
    const safeFilename =
      (filename || '').trim() || deriveFilenameFromUrl(url) || 'download';
    const downloadUrl = buildFilesProxyUrl({
      url,
      filename: safeFilename,
      disposition: 'attachment',
      // Prefer redirect so large files download directly from Storage (avoids proxy limits / buffering).
      redirect: true,
    });

    const resolvedDownloadUrl = new URL(downloadUrl, window.location.href);
    /**
     * Always use a top-level navigation:
     * - Avoids XHR/fetch CORS complexity
     * - Avoids buffering large blobs in-memory
     * - Works with redirect mode (backend -> Storage) for big media
     */
    const a = document.createElement('a');
    a.href = resolvedDownloadUrl.toString();
    a.target = '_blank';
    a.rel = 'noreferrer';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading file via API:', error);
    // Do NOT fall back to the raw Firebase Storage URL here: that typically
    // causes same-tab navigation + CORS errors. At this point we just fail loudly.
  }
}
